import React, { useState, useCallback, useRef, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useGoogleLogin } from "@react-oauth/google";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import useZustandStore from "../../../zustandStore/useZustandStore";
import AgencyAPIs from "../../../apiRoutes/Agency";
import { agencyDisplayName, getAgencyIdFromUser } from "../../../utils/agencyScope";
import { clipboardGrid } from "../../../utils/clipboard";
import AdsAPIs from "../../../apiRoutes/Ads";
import LoginAPIs from "../../../apiRoutes/Login";
import usePermissions from "../../../hooks/usePermissions";
import {  AGENCY_LIST_PERMISSIONS } from "../../../utils/constants/permissions";
import { GET_AGENCIES_QUERY_KEY, GET_AD_REQUESTS_QUERY_KEY, GET_AD_DELIVERIES_QUERY_KEY } from "../../../utils/constants/queryKeys";
import CustomModal from "../../common/CustomModal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import toast from "react-hot-toast";
import { validateAssetUrl } from "../utils/validateAssetUrl";
import { buildDeliveryAdName, isLegacyName } from "../../../utils/adNameBuilder";

const GOOGLE_SHEETS_SCOPES = "https://www.googleapis.com/auth/spreadsheets https://www.googleapis.com/auth/drive.file";

const isGoogleTokenRequired = (err) =>
  err?.response?.data?.error_code === "GOOGLE_TOKEN_REQUIRED";

const DISPLAY_KEYS = [
  "ad_id",
  "ad_request_id",
  "ad_asset_url",
  "ad_type",
  "creator_name",
  "editor_name",
  "adset_name",
  "ad_name",
  "topic",
  "app_name",
  "account",
  "request_created_at",
  "requested_by",
  "priority",
  "due_delivery_date",
  "requested_ad_type",
  "module",
  "category",
  "track",
  "script",
  "format_required",
  "reference_link",
  "example_reference_link",
  "comments",
];
const DISPLAY_LABELS = [
  "Ad id",
  "Ad request id",
  "Drive link",
  "Ad type",
  "Creator name",
  "Editor name",
  "Ad Set Name",
  "Ad name",
  "Topic",
  "App name",
  "Account",
  "Requested on",
  "Requested by",
  "Priority",
  "Due date",
  "Requested ad type",
  "Module",
  "Category",
  "Track",
  "Script",
  "Format required",
  "Reference link",
  "Example link",
  "Comments",
];

// Columns prefilled from the ad request for context only — not part of the create-delivery payload.
// Ad-level fields (module/category/track/topic/adset_name/etc.) stay editable so
// the delivery team can set them on orphan Ads or correct them on existing Ads.
const READONLY_KEYS = new Set([
  "request_created_at",
  "requested_by",
  "priority",
  "due_delivery_date",
  "requested_ad_type",
  "script",
  "format_required",
  "reference_link",
  "example_reference_link",
  "comments",
]);

function createEmptyRow() {
  return {
    ad_id: "",
    ad_request_id: "",
    request_created_at: "",
    requested_by: "",
    priority: "",
    due_delivery_date: "",
    requested_ad_type: "",
    topic: "",
    app_name: "",
    account: "",
    adset_name: "",
    module: "",
    category: "",
    track: "",
    script: "",
    format_required: "",
    reference_link: "",
    example_reference_link: "",
    comments: "",
    ad_name: "",
    ad_asset_url: "",
    ad_type: "",
    creator_name: "",
    editor_name: "",
  };
}

// A v2 request prefills creatorname/editorname placeholders in its names; legacy and orphan rows do not.
const isV2Row = (row) => !!(row.adset_name || "").trim() && !isLegacyName(row.adset_name);
const AGENCY_SLOT_KEYS = ["creator_name", "editor_name", "ad_type"];
// Rows tied to a request: only these cells are the agency's, the rest come from the request.
const AGENCY_EDITABLE_KEYS = new Set(["ad_asset_url", "ad_type", "creator_name", "editor_name"]);
const isRequestRow = (row) => parseId(row.ad_request_id) != null;
const isLockedCell = (row, key) => READONLY_KEYS.has(key) || (isRequestRow(row) && !AGENCY_EDITABLE_KEYS.has(key));

const parseId = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  if (Number.isInteger(n) && n > 0) return n;
  return null;
};

// step: "agency" -> "sheets" -> "table"
const BulkUploadExcelModal = ({ open, onClose }) => {
  const [agencyId, setAgencyId] = useState("");
  const [step, setStep] = useState(null);
  const [rows, setRows] = useState([]);
  const [selectedRowIndices, setSelectedRowIndices] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sheetId, setSheetId] = useState(null);
  const [isCreatingSheet, setIsCreatingSheet] = useState(false);
  const [isImportingSheet, setIsImportingSheet] = useState(false);
  const tableRef = useRef(null);
  const axiosPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();
  const currentUser = useZustandStore((s) => s?.currentUser);
  const { hasPermission } = usePermissions();
  const canListAgencies = AGENCY_LIST_PERMISSIONS.some((p) => hasPermission(p));
  const agencyIdFromUser = getAgencyIdFromUser(currentUser);
  const roles = currentUser?.roles;
  const isSuperAdmin = Array.isArray(roles) ? roles.includes("super_admin") : roles === "super_admin";
  const showAgencyDropdown = !agencyIdFromUser && isSuperAdmin;
  const effectiveAgencyId = agencyIdFromUser || (agencyId ? Number(agencyId) : null);

  useEffect(() => {
    if (open && step === null) {
      setStep(agencyIdFromUser ? "sheets" : "agency");
    }
  }, [open, step, agencyIdFromUser]);

  const { data: agenciesResult, isLoading: agenciesLoading } = useQuery({
    queryKey: GET_AGENCIES_QUERY_KEY,
    queryFn: () => AgencyAPIs.list({ axiosPrivate, status: "approved", limit: 200, offset: 0 }),
    enabled: open && showAgencyDropdown && canListAgencies,
  });
  const agencies = agenciesResult?.data ?? [];

  const { data: adTypesResult, isLoading: adTypesLoading } = useQuery({
    queryKey: ["adtypes-for-agency", effectiveAgencyId],
    queryFn: () => AdsAPIs.listAdTypesForAgency({ axiosPrivate, agency_id: effectiveAgencyId }),
    enabled: open && !!effectiveAgencyId,
  });
  const adTypeOptions = adTypesResult?.data ?? [];

  const handleNext = () => {
    if (!effectiveAgencyId) return toast.error(agencyIdFromUser ? "Your account is not linked to an agency." : "Select an agency.");
    setStep("sheets");
  };

  const buildPayload = (row, deliveredByAgencyId) => {
    const adAssetUrl = (row.ad_asset_url || "").trim();
    if (!adAssetUrl) return null;
    const adId = parseId(row.ad_id);
    const adRequestId = parseId(row.ad_request_id);
    const payload = {
      ad_asset_url: adAssetUrl,
      delivered_by_agency_id: deliveredByAgencyId,
    };
    if (adId != null) payload.ad_id = adId;
    if (adRequestId != null) payload.ad_request_id = adRequestId;
    const account = (row.account || "").trim();
    const adsetName = (row.adset_name || "").trim();
    const topic = (row.topic || "").trim();
    const appName = (row.app_name || "").trim();
    const adName = (row.ad_name || "").trim();
    const adType = (row.ad_type || "").trim();
    const creatorName = (row.creator_name || "").trim();
    const editorName = (row.editor_name || "").trim();
    const module = (row.module || "").trim();
    const category = (row.category || "").trim();
    const track = (row.track || "").trim();
    if (adType) payload.ad_type = adType;
    if (creatorName) payload.creator_name = creatorName;
    if (editorName) payload.editor_name = editorName;
    // Request rows send only the agency's cells; the backend takes everything else from the request.
    if (adRequestId != null) return payload;
    if (account) payload.account = account;
    if (adsetName) payload.adset_name = adsetName;
    if (topic) payload.topic = topic;
    if (appName) payload.for_app_name = appName;
    if (adName) payload.ad_name = adName;
    if (module) payload.module = module;
    if (category) payload.category = category;
    if (track) payload.track = track;
    return payload;
  };

  const handleSubmit = async () => {
    if (!effectiveAgencyId) return toast.error("Select an agency.");
    const rowsWithLink = rows
      .map((row, i) => ({ row, i }))
      .filter(({ row }) => (row.ad_asset_url || "").trim() !== "");
    if (!rowsWithLink.length) return toast.error("No rows with Drive link to submit.");
    const badLink = rowsWithLink.find(({ row }) => !validateAssetUrl(row.ad_asset_url).ok);
    if (badLink) {
      return toast.error(`Row ${badLink.i + 1}: invalid Drive link (folder links are not allowed).`);
    }
    const missingAdType = rowsWithLink.find(({ row }) => !(row.ad_type || "").trim());
    if (missingAdType) {
      return toast.error(`Row ${missingAdType.i + 1}: ad_type is required.`);
    }
    const missingSlots = rowsWithLink.find(({ row }) => isV2Row(row) && !(row.editor_name || "").trim());
    if (missingSlots) {
      return toast.error(`Row ${missingSlots.i + 1}: editor name is required.`);
    }
    const payloads = rowsWithLink
      .map(({ row }) => buildPayload(row, effectiveAgencyId))
      .filter(Boolean);
    if (!payloads.length) return toast.error("No valid rows to submit.");
    setIsSubmitting(true);
    try {
      const { count } = await AdsAPIs.createDeliveriesBulk({ axiosPrivate, deliveries: payloads });
      queryClient.invalidateQueries({ queryKey: GET_AD_DELIVERIES_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: GET_AD_REQUESTS_QUERY_KEY });
      toast.success(`${count} delivery/deliveries created.`);
      handleClose();
    } catch (err) {
      const msg = err?.response?.data?.error_message || err?.message;
      toast.error(`Create failed. ${msg || ""}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const retryCallbackRef = useRef(null);

  const onAuthCodeReceived = useCallback(async (code) => {
    try {
      await LoginAPIs.saveGoogleSheetsToken({ axiosPrivate, code });
    } catch {
      toast.error("Failed to save Google authorization.");
      setIsCreatingSheet(false);
      setIsImportingSheet(false);
      return;
    }
    const retry = retryCallbackRef.current;
    retryCallbackRef.current = null;
    if (retry) retry();
  }, [axiosPrivate]);

  const requestGoogleAuth = useGoogleLogin({
    flow: "auth-code",
    scope: GOOGLE_SHEETS_SCOPES,
    onSuccess: (codeResponse) => onAuthCodeReceived(codeResponse.code),
    onError: () => {
      toast.error("Google authorization failed. Please try again.");
      retryCallbackRef.current = null;
      setIsCreatingSheet(false);
      setIsImportingSheet(false);
    },
  });

  const createSheet = async () => {
    if (!effectiveAgencyId) return toast.error("Select an agency first.");
    setIsCreatingSheet(true);
    try {
      const result = await AdsAPIs.createDeliverySheet({ axiosPrivate, agency_id: effectiveAgencyId });
      if (result?.sheet_id && result?.sheet_url) {
        setSheetId(result.sheet_id);
        window.open(result.sheet_url, "_blank");
        toast.success("Google Sheet created and opened in a new tab.");
      } else {
        toast.error("Failed to create Google Sheet.");
      }
      setIsCreatingSheet(false);
    } catch (err) {
      if (isGoogleTokenRequired(err)) {
        retryCallbackRef.current = createSheet;
        requestGoogleAuth();
      } else {
        toast.error(err?.response?.data?.error_message || err?.message || "Failed to create sheet.");
        setIsCreatingSheet(false);
      }
    }
  };

  const importFromSheet = async () => {
    if (!sheetId) return toast.error("Create a Google Sheet first.");
    setIsImportingSheet(true);
    try {
      const importedRows = await AdsAPIs.importDeliverySheet({ axiosPrivate, sheet_id: sheetId });
      if (!Array.isArray(importedRows) || importedRows.length === 0) {
        toast.error("No data found in the sheet.");
        setIsImportingSheet(false);
        return;
      }
      const mapped = importedRows.map((r) => {
        const row = createEmptyRow();
        for (const key of DISPLAY_KEYS) {
          row[key] = r[key] ?? "";
        }
        return row;
      });
      setRows(mapped);
      setSelectedRowIndices(mapped.map((_, i) => i));
      toast.success(`Imported ${mapped.length} rows from Google Sheet.`);
      setIsImportingSheet(false);
      setStep("table");
    } catch (err) {
      if (isGoogleTokenRequired(err)) {
        retryCallbackRef.current = importFromSheet;
        requestGoogleAuth();
      } else {
        toast.error(err?.response?.data?.error_message || err?.message || "Failed to import sheet.");
        setIsImportingSheet(false);
      }
    }
  };

  const handleClose = () => {
    setAgencyId("");
    setStep(null);
    setRows([]);
    setSelectedRowIndices([]);
    setSheetId(null);
    onClose();
  };

  const toggleRow = (i) => {
    setSelectedRowIndices((prev) =>
      prev.includes(i) ? prev.filter((j) => j !== i) : [...prev, i].sort((a, b) => a - b)
    );
  };

  const toggleSelectAll = () => {
    if (selectedRowIndices.length === rows.length) setSelectedRowIndices([]);
    else setSelectedRowIndices(rows.map((_, i) => i));
  };

  const updateCell = useCallback((rowIndex, key, value) => {
    setRows((prev) =>
      prev.map((r, idx) => {
        if (idx !== rowIndex) return r;
        const next = { ...r, [key]: value };
        // Creator/editor/ad type fill their placeholders in the ad set and hook names live.
        if (AGENCY_SLOT_KEYS.includes(key) && (next.adset_name || "").trim()) {
          const slots = { creator_name: next.creator_name, editor_name: next.editor_name, ad_type: next.ad_type };
          next.adset_name = buildDeliveryAdName(next.adset_name, slots);
          if ((next.ad_name || "").trim()) next.ad_name = buildDeliveryAdName(next.ad_name, slots);
        }
        return next;
      })
    );
  }, []);

  const addRow = () => {
    setRows((prev) => [...prev, createEmptyRow()]);
    setSelectedRowIndices((prev) => [...prev, rows.length]);
  };

  const handleTablePaste = useCallback((e) => {
    const pastedGrid = clipboardGrid(e.clipboardData);
    if (!pastedGrid) return;
    const target = e.target;
    const td = target.closest("td");
    if (!td) return;
    const tr = td.closest("tr");
    if (!tr) return;
    const tbody = tr.closest("tbody");
    if (!tbody) return;
    const allRows = Array.from(tbody.querySelectorAll("tr"));
    const startRowIdx = allRows.indexOf(tr);
    const tds = Array.from(tr.querySelectorAll("td"));
    const startColIdx = tds.indexOf(td) - 1;
    e.preventDefault();
    setRows((prev) => {
      const next = [...prev];
      const totalRowsNeeded = startRowIdx + pastedGrid.length;
      while (next.length < totalRowsNeeded) next.push(createEmptyRow());
      for (let r = 0; r < pastedGrid.length; r++) {
        for (let c = 0; c < pastedGrid[r].length; c++) {
          const rowIdx = startRowIdx + r;
          const colIdx = startColIdx + c;
          if (colIdx >= 0 && colIdx < DISPLAY_KEYS.length) {
            const key = DISPLAY_KEYS[colIdx];
            if (isLockedCell(next[rowIdx], key)) continue;
            next[rowIdx] = { ...next[rowIdx], [key]: pastedGrid[r][c] };
          }
        }
      }
      return next;
    });
    setSelectedRowIndices((prev) => {
      const newIndices = new Set(prev);
      for (let r = 0; r < pastedGrid.length; r++) newIndices.add(startRowIdx + r);
      return Array.from(newIndices).sort((a, b) => a - b);
    });
  }, []);

  const rowsWithDriveLink = rows.filter((r) => (r.ad_asset_url || "").trim() !== "");
  const invalidLinkCount = rowsWithDriveLink.filter((r) => !validateAssetUrl(r.ad_asset_url).ok).length;

  if (!open) return null;

  return (
    <>
      {step === "agency" && (
        <CustomModal open title="Bulk Create Deliveries" onClose={handleClose} isButtonsRequired={false}>
          <div className="space-y-4 py-2">
            {showAgencyDropdown && (
              <div className="space-y-2">
                <Label>
                  Agency <span className="text-red-500">*</span>
                </Label>
                <Select value={agencyId} onValueChange={setAgencyId} disabled={agenciesLoading}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select agency" />
                  </SelectTrigger>
                  <SelectContent>
                    {(agencies ?? []).map((a, i) => (
                      <SelectItem key={a.id} value={String(a.id)}>{agencyDisplayName(a, i)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {!agencyIdFromUser && !isSuperAdmin && (
              <p className="text-sm text-muted-foreground">Your account is not linked to an agency. Contact an administrator.</p>
            )}
            <div className="flex gap-2 justify-end pt-2">
              <Button type="button" variant="outline" onClick={handleClose}>Cancel</Button>
              <Button type="button" onClick={handleNext} disabled={!effectiveAgencyId}>Next</Button>
            </div>
          </div>
        </CustomModal>
      )}

      {step === "sheets" && (
        <CustomModal open title="Bulk Create Deliveries" onClose={handleClose} isButtonsRequired={false}>
          <div className="flex flex-col items-center gap-5 py-8">
            <ul className="text-sm text-gray-600 text-left max-w-sm mx-auto space-y-1.5 list-disc list-inside">
              <li>Open the sheet → add <strong>Drive link</strong>, <strong>Ad type</strong>, <strong>Creator name</strong> and <strong>Editor name</strong> per row you’re delivering.</li>
              <li>Multiple deliveries? Duplicate the row with new ad name and drive link on each.</li>
              <li>Import here when done, then review and submit.</li>
            </ul>
            <Button
              type="button"
              onClick={createSheet}
              disabled={isCreatingSheet}
            >
              {isCreatingSheet ? "Creating Sheet…" : "Open in Google Sheets"}
            </Button>
            {sheetId && (
              <Button
                type="button"
                variant="outline"
                onClick={importFromSheet}
                disabled={isImportingSheet}
              >
                {isImportingSheet ? "Importing…" : "Import from Sheet"}
              </Button>
            )}
          </div>
          <div className="border-t border-gray-200 pt-3 flex justify-end">
            <Button type="button" variant="outline" onClick={agencyIdFromUser ? handleClose : () => setStep("agency")}>
              {agencyIdFromUser ? "Cancel" : "Back"}
            </Button>
          </div>
        </CustomModal>
      )}

      {step === "table" && (
        <CustomModal
          open
          title={`${rows.length} rows imported`}
          onClose={handleClose}
          isButtonsRequired={false}
          className="!w-[95%] !max-w-6xl md:!w-[90%] lg:!w-[85%]"
        >
          <div className="max-h-[65vh] overflow-auto" ref={tableRef} onPaste={handleTablePaste}>
            <table className="w-full text-left text-sm border-collapse border border-gray-200">
              <thead className="sticky top-0 bg-white z-10">
                <tr>
                  <th className="py-2 px-2 w-10 border border-gray-200">
                    <input
                      type="checkbox"
                      checked={rows.length > 0 && selectedRowIndices.length === rows.length}
                      onChange={toggleSelectAll}
                      className="rounded border-gray-300"
                    />
                  </th>
                  {DISPLAY_LABELS.map((label) => (
                    <th key={label} className="py-2 px-2 font-medium text-gray-600 whitespace-nowrap border border-gray-200">{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} className="hover:bg-gray-50/50">
                    <td className="py-1 px-2 w-10 border border-gray-200">
                      <input
                        type="checkbox"
                        checked={selectedRowIndices.includes(i)}
                        onChange={() => toggleRow(i)}
                        className="rounded border-gray-300"
                      />
                    </td>
                    {DISPLAY_KEYS.map((key) => (
                      <td key={key} className="py-0 px-0 border border-gray-200 align-top">
                        {isLockedCell(row, key) ? (
                          <div
                            className="px-2 py-1.5 text-sm text-gray-700 bg-gray-50/60 max-w-[260px] truncate"
                            title={row[key] ?? ""}
                          >
                            {row[key] ?? ""}
                          </div>
                        ) : key === "ad_type" ? (
                          <Select
                            value={row.ad_type || ""}
                            onValueChange={(v) => updateCell(i, "ad_type", v)}
                            disabled={adTypesLoading || adTypeOptions.length === 0}
                          >
                            <SelectTrigger className="w-full min-w-[140px] border-0 rounded-none h-auto py-1.5 px-2 text-sm focus:ring-2 focus:ring-ring focus:ring-inset">
                              <SelectValue placeholder={adTypesLoading ? "Loading…" : "Select ad type"} />
                            </SelectTrigger>
                            <SelectContent>
                              {adTypeOptions.map((t) => (
                                <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : key === "ad_asset_url" ? (
                          (() => {
                            const trimmed = (row.ad_asset_url || "").trim();
                            const { ok, error } = validateAssetUrl(row.ad_asset_url);
                            const showError = !!trimmed && !ok && !!error;
                            return (
                              <div className="min-w-[200px]">
                                <input
                                  value={row.ad_asset_url ?? ""}
                                  onChange={(e) => updateCell(i, "ad_asset_url", e.target.value)}
                                  aria-invalid={showError}
                                  className={`w-full min-w-0 bg-transparent py-1.5 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-inset ${
                                    showError ? "ring-2 ring-red-400 ring-inset" : "focus:ring-ring"
                                  }`}
                                />
                                {showError && <p className="px-2 pb-1 text-xs text-red-600">{error}</p>}
                              </div>
                            );
                          })()
                        ) : (
                          <input
                            value={row[key] ?? ""}
                            onChange={(e) => updateCell(i, key, e.target.value)}
                            className="w-full min-w-0 bg-transparent py-1.5 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-inset"
                          />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {invalidLinkCount > 0 && (
            <p className="pt-2 text-sm text-red-600">
              {invalidLinkCount} row{invalidLinkCount !== 1 ? "s have" : " has"} an invalid Drive link (folder links are not allowed). Fix them before creating.
            </p>
          )}
          <div className="border-t border-gray-200 pt-3 flex items-center justify-between mt-2">
            <Button type="button" variant="outline" size="sm" onClick={addRow}>+ Add row</Button>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setStep("sheets")}>Back</Button>
              <Button type="button" onClick={handleSubmit} disabled={isSubmitting || rowsWithDriveLink.length === 0 || invalidLinkCount > 0}>
                {isSubmitting ? "Creating…" : `Create (${rowsWithDriveLink.length})`}
              </Button>
            </div>
          </div>
        </CustomModal>
      )}
    </>
  );
};

export default BulkUploadExcelModal;
